import { Button, Input, Modal, Row, Select, Tooltip, Typography } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { trainingAPI } from "@api/TrainingAPI";
import { useDispatch, useSelector } from "react-redux";
import { getManagerTeamList } from "@store/actions/manager.actions";
import { getTeamInfo } from "@store/actions/team.actions";
import React from "react";
import ReactMarkdown from "react-markdown";

const { Text } = Typography;

export const ThirdPartyIntegrationModal = ({
    visibility,
    setVisibility,
    service,
    setService,
    thirdPartyIntegrationModalData,
    setThirdPartyIntegrationModalData,
    modalStatus,
    refetchTableData,
    servicesWithKey
}: IThirdParthOnboardingData) => {
    const dispatch = useDispatch();

    const {
        misc: { third_party_connections_form_data },
    } = useSelector(({ team }) => team);

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [overrideFields, setOverrideFields] = React.useState<Record<string, boolean>>({});

    React.useEffect(() => {
        setOverrideFields({});
    }, [service]);

    const formData: {
        [key: string]: {
            name: string;
            description?: string;
            fields: {
                placeholder: string | undefined;
                display: string,
                key: string,
                param: string,
                required: boolean,
                prefix?: string,
                suffix?: string,
                strip_prefixes?: string[],
                options?: { value: string, label: string, aliases?: string[] }[],
                custom_override?: {
                    param: string;
                    label: string;
                    revert_label?: string;
                    display?: string;
                    placeholder?: string
                }
            }[]
        }
    } = third_party_connections_form_data

    const modalData = thirdPartyIntegrationModalData as Record<string, unknown>;

    // an option list may name the IANA timezones it covers. The browser reports one of
    // them, so the modal opens with the timezone of the user already selected.
    React.useEffect(() => {
        if (!service) {
            return;
        }

        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const defaults: Record<string, string> = {};

        formData[service].fields.forEach((field) => {
            const current = modalData[field.param];
            if (!field.options || (current != null && String(current).trim() !== "")) {
                return;
            }

            const match = field.options.find(
                (option) => option.value === browserTimezone || option.aliases?.includes(browserTimezone)
            );
            if (match) {
                defaults[field.param] = match.value;
            }
        });

        if (Object.keys(defaults).length) {
            setThirdPartyIntegrationModalData({ ...modalData, ...defaults });
        }
        // the parent clears the data whenever the service changes, so this runs on a clean form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service]);

    // a field that renders behind an addon (such as "https://") tells us which prefixes a
    // paste may duplicate. The backend strips them too, so this is only for what the user sees.
    const stripLeadingPrefix = (text: string, prefixes?: string[]) => {
        const match = prefixes?.find((p) => text.toLowerCase().startsWith(p.toLowerCase()));
        return match ? text.slice(match.length).trimStart() : text;
    };

    const onChangeText = (param: string, text: string, stripPrefixes?: string[]) => {
        setThirdPartyIntegrationModalData({
            ...thirdPartyIntegrationModalData,
            [param]: stripLeadingPrefix(text, stripPrefixes)
        })
    }

    const toggleOverride = (field: { key: string; param: string; custom_override?: { param: string } }) => {
        const turningOn = !overrideFields[field.key];
        setOverrideFields({ ...overrideFields, [field.key]: turningOn });

        const paramToClear = turningOn ? field.param : field.custom_override?.param;
        if (paramToClear) {
            const next = { ...modalData };
            delete next[paramToClear];
            setThirdPartyIntegrationModalData(next);
        }
    };

    const submitModalData = async () => {
        setIsLoading(true);
        try {
            // copy-pasted credentials routinely carry stray whitespace; passwords are
            // left untouched since leading/trailing spaces can be intentional there
            const payload = Object.fromEntries(
                Object.entries(modalData).map(([param, value]) => [
                    param,
                    typeof value === "string" && param !== "password" ? value.trim() : value
                ])
            );

            if (modalStatus === 'add') {
                await trainingAPI.postThirdPartyOnboarding({ ...payload, service })
            } else {
                await trainingAPI.patchThirdPartyOnboarding({ ...payload, service })
            }

            await refetchTableData()
            dispatch(getTeamInfo());

            message.success("External data source connected successfully");
            setVisibility(false)
        } catch (err) {
            message.error((err as any).response.data)
        } finally {
            setIsLoading(false);
        }
    }

    const activeParam = (field: { key: string; param: string; custom_override?: { param: string } }) =>
        overrideFields[field.key] && field.custom_override ? field.custom_override.param : field.param;

    const hasMissingRequiredFields = () => {
        if (!service) {
            return true;
        }

        return formData[service].fields.some((field) => {
            if (!field.required) {
                return false;
            }
            const value = modalData[activeParam(field)];
            return value == null || String(value).trim() === "";
        });
    };

    return (
        <>
            <Modal
                title="Setup a New Data Connection"
                open={visibility}
                footer={[
                    // <Button key="cancel" onClick={() => setVisibility(false)}>
                    //     Cancel
                    // </Button>,
                    <Tooltip key="ok-tooltip" title={hasMissingRequiredFields() && "Missing fields"}>
                        <Button
                            type="primary"
                            onClick={submitModalData}
                            disabled={hasMissingRequiredFields()}
                            style={{ minWidth: 80, marginLeft: '10px' }}
                            loading={isLoading}
                        >
                            Connect
                        </Button>
                    </Tooltip>
                ]}
                onCancel={() => setVisibility(false)}
                width={600}
                bodyStyle={{ height: '400px', overflow: 'hidden' }}
            >
                <div style={{ height: '100%', overflowY: 'auto', paddingRight: '8px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <Row style={{ marginBottom: '10px' }}>
                            <Text>External Data Source</Text>
                        </Row>

                        <Row>
                            <Select
                                style={{ minWidth: '100%' }}
                                showSearch
                                placeholder="Please select an external data source"
                                value={service}
                                disabled={modalStatus === 'edit'}
                                onChange={(e: string) => {
                                    setService(e)
                                    setThirdPartyIntegrationModalData({})
                                }}
                                options={Object.keys(formData).filter((e) => {
                                    if (modalStatus === 'add' && servicesWithKey && servicesWithKey.includes(e)) {
                                        return undefined
                                    }

                                    return e
                                }).sort((a, b) => formData[a]?.name.localeCompare(formData[b]?.name)).map((e) => {
                                    return {
                                        value: e,
                                        label: formData[e].name
                                    }
                                })}
                            />
                        </Row>
                    </div>

                    {service && formData[service].description && (
                        <Row style={{ marginBottom: '10px' }}>
                            <ReactMarkdown>{formData[service].description}</ReactMarkdown>
                        </Row>
                    )}

                    {service &&
                        formData[service].fields.map((fieldData) => {
                            const isOverride = !!overrideFields[fieldData.key] && !!fieldData.custom_override;
                            return (
                                <div style={{ marginBottom: '10px' }} key={`${service}-${fieldData['key']}`}>
                                    <Row style={{ marginBottom: '10px' }}>
                                        <Text>
                                            {(isOverride && fieldData.custom_override?.display) || fieldData.display}
                                        </Text>
                                    </Row>

                                    <Row>
                                        {isOverride && fieldData.custom_override ? (
                                            <Input
                                                style={{ minWidth: "100%" }}
                                                allowClear
                                                value={(modalData[fieldData.custom_override.param] as string) ?? ""}
                                                onChange={(e) => onChangeText(fieldData.custom_override!.param, e.target.value)}
                                                placeholder={fieldData.custom_override.placeholder || ""}
                                            />
                                        ) : fieldData.options ? (
                                            // a field the backend restricts to a known set, such as the
                                            // timezone. A typed name stops every upload, so do not accept one.
                                            <Select
                                                style={{ minWidth: "100%" }}
                                                showSearch
                                                allowClear
                                                optionFilterProp="label"
                                                value={(modalData[fieldData.param] as string) || undefined}
                                                onChange={(value: string | undefined) => onChangeText(fieldData.param, value ?? "")}
                                                placeholder={fieldData.placeholder || ""}
                                                options={fieldData.options}
                                            />
                                        ) : (
                                            <Input
                                                style={{ minWidth: "100%" }}
                                                allowClear
                                                addonBefore={fieldData.prefix}
                                                addonAfter={fieldData.suffix}
                                                value={(modalData[fieldData.param] as string) ?? ""}
                                                onChange={(e) => onChangeText(fieldData['param'], e.target.value, fieldData.strip_prefixes)}
                                                placeholder={fieldData.placeholder || ""}
                                            />
                                        )}
                                    </Row>

                                    {fieldData.custom_override && (
                                        <Row style={{ marginTop: '4px' }}>
                                            <Button
                                                type="link"
                                                size="small"
                                                style={{ padding: 0 }}
                                                onClick={() => toggleOverride(fieldData)}
                                            >
                                                {isOverride
                                                    ? (fieldData.custom_override.revert_label || "Use the templated field instead")
                                                    : fieldData.custom_override.label}
                                            </Button>
                                        </Row>
                                    )}
                                </div>
                            )
                        })
                    }
                </div>
            </Modal>
        </>
    )
}

interface IThirdParthOnboardingData {
    visibility: boolean;
    setVisibility: React.Dispatch<boolean>
    service: string | undefined,
    setService: React.Dispatch<string>,
    thirdPartyIntegrationModalData: object,
    setThirdPartyIntegrationModalData: React.Dispatch<object>,
    modalStatus: "add" | "edit",
    refetchTableData: () => Promise<void>;
    servicesWithKey?: string[]
}

export default ThirdPartyIntegrationModal